import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import {
  Mail,
  Zap,
  Database,
  FileText,
  Bell,
  MessageSquare,
  Clock,
  RefreshCw,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export type ActionType =
  | 'email'
  | 'webhook'
  | 'database'
  | 'document'
  | 'notification'
  | 'sms'
  | 'delay'
  | 'loop'
  | 'custom'

export interface ActionShapeNodeData {
  label: string
  actionType: ActionType
  status?: 'idle' | 'configured' | 'executing' | 'success' | 'error'
  subtitle?: string
  errorMessage?: string
  // Email action fields
  emailTo?: string
  emailSubject?: string
  emailTemplate?: string
  // Webhook action fields
  webhookUrl?: string
  webhookMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  // Database action fields
  databaseTable?: string
  databaseOperation?: 'insert' | 'update' | 'delete' | 'query'
  // Document action fields
  templateId?: string
  templateName?: string
  outputFormat?: 'pdf' | 'docx' | 'html'
  // Custom action fields
  customActionId?: string
  customActionName?: string
  onDelete?: () => void
  [key: string]: unknown
}

const actionConfig: Record<ActionType, { icon: typeof Mail; color: string }> = {
  email: { icon: Mail, color: '#10b981' },
  webhook: { icon: Zap, color: '#06b6d4' },
  database: { icon: Database, color: '#6366f1' },
  document: { icon: FileText, color: '#f59e0b' },
  notification: { icon: Bell, color: '#ec4899' },
  sms: { icon: MessageSquare, color: '#14b8a6' },
  delay: { icon: Clock, color: '#8b5cf6' },
  loop: { icon: RefreshCw, color: '#f97316' },
  custom: { icon: Send, color: '#64748b' },
}

const statusIcons = {
  idle: null,
  configured: CheckCircle2,
  executing: Loader2,
  success: CheckCircle2,
  error: AlertCircle,
}

interface ActionShapeNodeProps {
  data: ActionShapeNodeData
  selected?: boolean
}

function ActionShapeNodeComponent({ data, selected }: ActionShapeNodeProps) {
  const { label, actionType, status = 'idle', subtitle, errorMessage } = data
  const config = actionConfig[actionType] || actionConfig.custom
  const Icon = config.icon
  const StatusIcon = statusIcons[status]

  return (
    <div
      className="relative group"
      style={{ width: 190, height: 80 }}
    >
      {/* Rounded rectangle with subtle skew - process/action style */}
      <svg
        viewBox="0 0 190 80"
        className="absolute inset-0 w-full h-full"
        style={{
          filter: selected
            ? `drop-shadow(0 0 12px ${config.color}40)`
            : 'drop-shadow(0 4px 6px rgba(0,0,0,0.07))',
        }}
      >
        <defs>
          <linearGradient id={`action-grad-${actionType}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={config.color} stopOpacity="0.15" />
            <stop offset="100%" stopColor={config.color} stopOpacity="0.05" />
          </linearGradient>
        </defs>
        {/* Rounded rectangle */}
        <rect
          x="3"
          y="3"
          width="184"
          height="74"
          rx="12"
          ry="12"
          fill={`url(#action-grad-${actionType})`}
          stroke={selected ? config.color : '#e5e7eb'}
          strokeWidth={selected ? 2.5 : 1.5}
          className="dark:stroke-gray-600"
        />
        {/* Left accent bar */}
        <rect
          x="3"
          y="3"
          width="4"
          height="74"
          rx="2"
          fill={config.color}
        />
      </svg>

      {/* Content */}
      <div className="absolute inset-0 flex items-center pl-5 pr-3">
        {/* Icon */}
        <div
          className="flex-shrink-0 p-2 rounded-lg mr-3"
          style={{ backgroundColor: `${config.color}15` }}
        >
          <Icon className="w-4.5 h-4.5" style={{ color: config.color }} />
        </div>

        {/* Text */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {label}
            </p>
            {StatusIcon && (
              <StatusIcon
                className={cn(
                  'w-3.5 h-3.5 flex-shrink-0',
                  status === 'executing' && 'animate-spin',
                  status === 'success' && 'text-emerald-500',
                  status === 'error' && 'text-red-500',
                  status === 'configured' && 'text-emerald-500'
                )}
              />
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {subtitle || getDefaultSubtitle(actionType)}
          </p>
        </div>
      </div>

      {/* Error indicator */}
      {status === 'error' && errorMessage && (
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 translate-y-full opacity-0 group-hover:opacity-100 transition-opacity z-50">
          <div className="mt-2 px-3 py-1.5 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg shadow-lg max-w-[200px]">
            <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Target Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-800 !-top-1.5"
        style={{ backgroundColor: config.color }}
      />

      {/* Source Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-800 !-bottom-1.5"
        style={{ backgroundColor: config.color }}
      />
    </div>
  )
}

function getDefaultSubtitle(actionType: ActionType): string {
  switch (actionType) {
    case 'email':
      return 'Send email notification'
    case 'webhook':
      return 'Call external API'
    case 'database':
      return 'Database operation'
    case 'document':
      return 'Generate document'
    case 'notification':
      return 'Push notification'
    case 'sms':
      return 'Send SMS message'
    case 'delay':
      return 'Wait before continuing'
    case 'loop':
      return 'Repeat steps'
    default:
      return 'Custom action'
  }
}

export const ActionShapeNode = memo(ActionShapeNodeComponent)
export default ActionShapeNode
